// src/portal-app/PortalAppShell.js
//
// Native app boot shell for the Aqana Manager (portal) flavor. Task 2
// shipped a minimal placeholder so the second binary boots; this task
// (Task 4) replaces the body with the real gates, mirroring
// src/pos-app/PosAppShell.js's backend-binding gate exactly, then branching
// on auth (manager login vs shell) instead of station config. Responsibilities:
//   1. Backend-binding gate — see PosAppShell.js for the full rationale.
//   2. Hide the native splash screen once we know what to render.
//   3. Render PortalLogin when unauthenticated, PortalShell when authenticated.
import React, { useEffect, useState } from 'react';
import host from '../host';
import { ensureBackendBinding } from '../platform/backendBinding';
import { useAuth } from '../context/AuthContext';
import PortalLogin from './PortalLogin';
import PortalShell from './PortalShell';
import { C, FONT } from '../styles/theme';

const screenStyle = {
    minHeight: '100vh',
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: C.bg,
    fontFamily: FONT,
    padding: 24,
    boxSizing: 'border-box',
};

const subtextStyle = {
    fontSize: 13,
    color: C.muted,
    margin: 0,
};

function LoadingView() {
    return (
        <div style={screenStyle}>
            <p style={subtextStyle}>Loading…</p>
        </div>
    );
}

function PortalAppShellInner() {
    const { isAuthenticated, isLoading } = useAuth();

    // Gate on the INITIAL auth resolution only (Task 5 fix). AuthContext's
    // login() flips isLoading true for the duration of every sign-in attempt,
    // so gating on raw isLoading would unmount PortalLogin mid-submit and —
    // on a failed attempt — remount it blank, eating the inline error and
    // everything the manager typed. (PosAppShell never reads isLoading, which
    // is why EnrollmentScreen doesn't hit this.) Once auth has settled once,
    // stay on the branch below and let the screens own their loading states.
    // Invariant this relies on: isLoading must be true on first render (true
    // for the real AuthContext). The latch opens on the first isLoading→false
    // and then ignores isLoading entirely, so a later login()-driven isLoading
    // flip can never unmount the login form again.
    const [authSettled, setAuthSettled] = useState(false);
    useEffect(() => {
        if (!isLoading) setAuthSettled(true);
    }, [isLoading]);

    useEffect(() => {
        if (isLoading) return;
        if (host.type !== 'capacitor') return;

        (async () => {
            try {
                const { SplashScreen } = await import('@capacitor/splash-screen');
                await SplashScreen.hide();
            } catch (err) {
                // Never let a missing/failed native plugin block the app.
            }
        })();
    }, [isLoading]);

    if (!authSettled) {
        return <LoadingView />;
    }

    if (!isAuthenticated) {
        return <PortalLogin />;
    }

    return <PortalShell />;
}

export default function PortalAppShell() {
    // Backend-binding gate (F12a-N Issue A) — see PosAppShell.js for the
    // full rationale. SECONDARY LAYER: the authoritative call happens in
    // src/index.js, awaited BEFORE `root.render()`, before AuthProvider (an
    // ANCESTOR of this component) ever mounts. This effect is kept as a
    // harmless, idempotent second check — ensureBackendBinding() no-ops
    // when the marker already matches — for any boot path that somehow
    // reaches this component without having gone through index.js's render
    // call (hot reload in dev, tests mounting PortalAppShell directly, etc.).
    const [bindingReady, setBindingReady] = useState(host.type !== 'capacitor');

    useEffect(() => {
        if (host.type !== 'capacitor') return;

        let cancelled = false;
        (async () => {
            try {
                await ensureBackendBinding();
            } catch (err) {
                // Never let a binding-check failure block boot.
            } finally {
                if (!cancelled) setBindingReady(true);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, []);

    if (!bindingReady) {
        return <LoadingView />;
    }

    return <PortalAppShellInner />;
}
